import {Router} from 'vue-router'
import {Pinia} from 'pinia'
import {initializeAppRouterHookFactory} from './router/hooks/initializeAppRouterHook'
import {ensureAuthRouterHookFactory} from './router/hooks/ensureAuthRouterHook'
import {markFirstRouteAsInitializedRouterHookFactory} from './router/hooks/markFirstRouteAsInitializedRouterHook'
import {ensureWorkspaceRouterHookFactory} from './router/hooks/ensureWorkspaceRouterHook'
import {ensureSuperAdminRouterHookFactory} from './router/hooks/ensureSuperAdminRouterHook'
import {ensureBillingEnabledRouterHookFactory} from './router/hooks/ensureBillingEnabledRouterHook'
import {setTransitionNameRouterHookFactory} from './router/hooks/setTransitionNameRouterHook'
import {forceDeploymentUpdateRouterHookFactory} from './router/hooks/forceDeploymentUpdateRouterHook'
import {preventNavigationRouterHookFactory} from './router/hooks/preventNavigationRouterHook'
import {finishPageLoadingRouterHookFactory} from '@toolify/client/src/setup/router/hooks/PageLoadingHooks/finishPageLoadingRouterHook'
import {startPageLoadingRouterHookFactory} from '@toolify/client/src/setup/router/hooks/PageLoadingHooks/startPageLoadingRouterHook'
import {
  startDebounceTimerRouterHookFactory,
} from '@toolify/client/src/setup/router/hooks/DebounceTimerHooks/startDebounceTimerRouterHook'
import {
  awaitDebounceTimerRouterHookFactory,
} from '@toolify/client/src/setup/router/hooks/DebounceTimerHooks/awaitDebounceTimerRouterHook'
import * as Sentry from '@sentry/vue'
import {redactSentryLog} from '@toolify/server/src/helpers/logRedaction/redactSentryPayloads'
import type {App} from 'vue'
import {
  beforeDatabaseViewPageRouterHookFactory,
} from '@toolify/client/src/pages/DatabaseViewPage/beforeDatabaseViewPageRouterHook'
import {checkRecentLinkToNavigateRouterHookFactory} from './router/hooks/checkRecentLinkToNavigateRouterHookFactory'
import {handleStackableRouteRouterHookFactory} from './router/hooks/handleStackableRouteRouterHook'
import {markResourceAsReadRouterHookFactory} from './router/hooks/markResourceAsReadRouterHook'
import {saveLastVisitedResourceRouterHook} from './router/hooks/saveLastVisitedResourceRouterHook'
import {syncWorkspaceTabsRouterHook} from './router/hooks/syncWorkspaceTabsRouterHook'
import {trackRouteLayerRouterHookFactory} from './router/hooks/trackRouteLayerRouterHook'
import {syncSpaceFromRouteRouterHookFactory} from './router/hooks/syncSpaceFromRouteRouterHook'
import {
  ensureOnboardingCompleteRouterHookFactory,
} from './router/hooks/ensureOnboardingCompleteRouterHookFactory'
import {hydrateLocalPreferenceRouterHookFactory} from './router/hooks/hydrateLocalPreferenceRouterHook'
import {applyGuestAppearanceRouterHookFactory} from './router/hooks/applyGuestAppearanceRouterHook'
import {useAppStore} from '@toolify/client/src/stores/AppStore/useAppStore'
import {applyStackableRouteParamInheritance} from './router/applyStackableRouteParamInheritance'
import {openDatabaseItemOverItsViewRouterHooksFactory} from './router/hooks/openDatabaseItemOverItsViewRouterHook'

export function runPostClientSetupTasks(
  router: Router,
  pinia: Pinia,
  vueApp: App,
) {
  const isDebounceEnabled = false
  applyStackableRouteParamInheritance(router, pinia)
  router.beforeEach(preventNavigationRouterHookFactory(pinia))
  router.beforeEach(forceDeploymentUpdateRouterHookFactory(pinia))
  if(isDebounceEnabled) {
    router.beforeEach(startDebounceTimerRouterHookFactory(pinia, 250))
  }
  router.beforeEach(checkRecentLinkToNavigateRouterHookFactory(pinia))
  router.beforeEach(startPageLoadingRouterHookFactory(pinia))
  router.beforeEach(initializeAppRouterHookFactory())
  router.beforeEach(applyGuestAppearanceRouterHookFactory(pinia))
  router.beforeEach(ensureAuthRouterHookFactory(pinia))
  router.beforeEach(hydrateLocalPreferenceRouterHookFactory(pinia))
  router.beforeEach(ensureSuperAdminRouterHookFactory(pinia))
  router.beforeEach(ensureWorkspaceRouterHookFactory(pinia, router))
  router.beforeEach(ensureBillingEnabledRouterHookFactory(pinia))
  router.beforeEach(syncSpaceFromRouteRouterHookFactory(pinia))
  router.beforeEach(ensureOnboardingCompleteRouterHookFactory(pinia))
  router.beforeEach(beforeDatabaseViewPageRouterHookFactory(pinia)) // TODO: Move to DatabaseViewPage in setupClientRoutes
  const openDatabaseItemOverItsViewRouterHooks = openDatabaseItemOverItsViewRouterHooksFactory(router)
  router.beforeEach(openDatabaseItemOverItsViewRouterHooks.beforeEach)
  
  if(isDebounceEnabled) {
    router.beforeEach(awaitDebounceTimerRouterHookFactory(pinia))
  }

  router.afterEach(handleStackableRouteRouterHookFactory(pinia))
  router.afterEach(openDatabaseItemOverItsViewRouterHooks.afterEach)
  router.afterEach(trackRouteLayerRouterHookFactory(pinia))
  router.afterEach(setTransitionNameRouterHookFactory())
  router.afterEach(markFirstRouteAsInitializedRouterHookFactory(pinia))
  router.afterEach(finishPageLoadingRouterHookFactory(pinia))
  router.afterEach(markResourceAsReadRouterHookFactory(pinia))
  router.afterEach(saveLastVisitedResourceRouterHook(pinia))
  router.afterEach(syncWorkspaceTabsRouterHook(pinia))

  router.onError((error) => {
    const appStore = useAppStore(pinia)
    appStore.isPageLoading = false
    appStore.isFirstRouteInitialized = true
    Sentry.captureException(error)
  })

  if(ENV.SentryDsn) {
    Sentry.init({
      release: `${ENV.ProjectName}@${ENV.Version}`,
      environment: ENV.EnvironmentName,
      app: vueApp,
      dsn: ENV.SentryDsn,
      integrations: [
        Sentry.browserTracingIntegration({
          ignorePerformanceApiSpans: [/^intlify /],
        }),
        Sentry.replayIntegration(),
        // Sentry.consoleLoggingIntegration({levels: ['log', 'error']}),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Sentry Logs for product events (the calendar's trackCalendarEvent), redacted like the server's logs
      enableLogs: true,
      beforeSendLog: log => redactSentryLog(log),
    })
  }
}
